import { Alert, AlertIcon, Spinner, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';

import type { FullProfileInfoData } from '../CustomDash/components/Chart/FullProfileInfo/types';
import { CareerSnapshot } from './CareerSnapshot';
import { CompanyCompareDrawer } from './company-details/CompanyCompareDrawer';
import { CompareSelectionTray } from './company-details/CompareSelectionTray';
import { BetterEmployersSection } from './explore/BetterEmployersSection';
import { CareerTrajectorySection } from './explore/CareerTrajectorySection';
import { GeographicMobilitySection } from './explore/GeographicMobilitySection';
import { useCompanyCompareStore } from './hooks/useCompanyCompareStore';
import { useCompanyRics } from './hooks/useCompanyRics';
import { useEmployerIntelligence } from './hooks/useEmployerIntelligence';
import { useMapUserSkills } from './hooks/useMapUserSkills';
import { useNameSearch } from './hooks/useNameSearch';
import { useResolveProfileIds } from './hooks/useResolveProfileIds';
import { useRoleSkillComposition } from './hooks/useRoleSkillComposition';
import {
  DEFAULT_TOGGLES,
  type SalaryToggles,
  useSalaryControlsStore,
} from './hooks/useSalaryControlsStore';
import { useUserPercentile } from './hooks/useUserPercentile';
import { EmployerOutlookSection } from './optimize/EmployerOutlookSection';
import { SalarySection } from './optimize/SalarySection';
import { SkillPayImpactSection } from './optimize/SkillPayImpactSection';
import { type ActiveTrack, TrackToggle } from './TrackToggle';

interface ResultsViewProps {
  searchName: string;
  selectedUser: TalentDiscoveryRespUserV3 | null;
  profileData: FullProfileInfoData | null;
  userSalary: number;
  salaryTypeLabel: 'Base' | 'TC';
  isSimulatedSalary: boolean;
  onProfileLoaded: (
    user: TalentDiscoveryRespUserV3,
    profile: FullProfileInfoData
  ) => void;
}

export const ResultsView = ({
  searchName,
  selectedUser,
  profileData,
  userSalary,
  salaryTypeLabel,
  isSimulatedSalary,
  onProfileLoaded,
}: ResultsViewProps) => {
  const [activeTrack, setActiveTrack] = useState<ActiveTrack>('optimize');

  // Salary controls from store (shared with top bar in IndividualPotentialPage)
  const upliftMode = useSalaryControlsStore((s) => s.upliftMode);
  const userPercentile = useSalaryControlsStore((s) => s.userPercentile);
  const setUserPercentile = useSalaryControlsStore((s) => s.setUserPercentile);

  // Salary toggles are only used by SalarySection — keep local
  const [salaryToggles, setSalaryToggles] =
    useState<SalaryToggles>(DEFAULT_TOGGLES);
  const handleToggleChange = useCallback((key: keyof SalaryToggles) => {
    setSalaryToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const {
    searchUser,
    profileData: fetchedProfile,
    isSearching,
    isLoadingProfile,
    error,
  } = useNameSearch(searchName, !selectedUser);

  useEffect(() => {
    if (searchUser && fetchedProfile && !selectedUser) {
      onProfileLoaded(searchUser, fetchedProfile);
    }
  }, [searchUser, fetchedProfile, selectedUser, onProfileLoaded]);

  const isLoading = isSearching || isLoadingProfile;
  const currentUser = selectedUser ?? searchUser;
  const currentProfile = profileData ?? fetchedProfile;

  // ── Core resolvers (shared across all sections) ──

  const { resolvedIds, isResolving } = useResolveProfileIds(currentUser);
  const { rics } = useCompanyRics(
    resolvedIds.companyName,
    resolvedIds.companyRcid
  );

  const userSkills = currentUser?.skills ?? currentProfile?.skills ?? [];
  const {
    resolvedSkills,
    unresolvedSkills,
    isLoading: isSkillsLoading,
  } = useMapUserSkills(userSkills);

  const userRoleLevel = useMemo(
    () =>
      resolvedIds.roleLevels.find((l) => l.type === 'role_k150') ??
      resolvedIds.roleLevels[0] ??
      null,
    [resolvedIds.roleLevels]
  );

  const userPrimaryGeo = useMemo(() => {
    return resolvedIds.primaryGeo ?? resolvedIds.geoLevels[0] ?? null;
  }, [resolvedIds.primaryGeo, resolvedIds.geoLevels]);

  // Pin current employer in compare store
  const setCurrentEmployer = useCompanyCompareStore(
    (s) => s.setCurrentEmployer
  );
  useEffect(() => {
    if (resolvedIds.companyRcid && resolvedIds.companyName) {
      setCurrentEmployer({
        rcid: resolvedIds.companyRcid,
        name: resolvedIds.companyName,
      });
    } else {
      setCurrentEmployer(null);
    }
  }, [resolvedIds.companyRcid, resolvedIds.companyName, setCurrentEmployer]);

  const { userPercentile: computedPercentile } = useUserPercentile(
    resolvedIds,
    userSalary
  );

  useEffect(() => {
    setUserPercentile(computedPercentile);
  }, [computedPercentile, setUserPercentile]);

  // ── Role skill composition (for CareerSnapshot skills count) ──

  const { roleSkills } = useRoleSkillComposition(resolvedIds);

  // ── Employer Intelligence (composite hook) ──

  const {
    workHistoryCompanies,
    competitorSourceOverride,
    handleCompetitorSourceChange,
    competitors,
    isIndustryFallback,
    metrics,
    sentiments,
    healthData,
    currentEmployerHealth,
    hiddenGems,
    leverageSignals,
    leverageRoleName,
    signalsLoading,
    signalsError,
    sentimentEffectsData,
    sentimentEffectsLoading,
    currentCompanyMetrics,
    careerMoveSteps,
    isDataLoading,
    isTableLoading,
  } = useEmployerIntelligence(currentUser, resolvedIds);

  // ── Render guards ──

  if (isLoading && !currentProfile) {
    return (
      <VStack spacing={4} align="center" py={12}>
        <Spinner size="lg" color="green.500" thickness="3px" />
        <Text color="navyBlue.300" fontSize="sm">
          {isSearching
            ? 'Searching for profile...'
            : 'Loading profile details...'}
        </Text>
      </VStack>
    );
  }

  if (error && !currentProfile) {
    return (
      <Alert
        status="warning"
        borderRadius="xl"
        mt={4}
        border="1px solid"
        borderColor="orange.200"
      >
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const hasUserData = currentProfile && currentUser;
  const hasCompany = !!resolvedIds.companyRcid;
  const hasRole = !!resolvedIds.roleId;

  return (
    <>
      <VStack spacing={6} align="stretch" pb={20}>
        {/* Career Snapshot (shared hero) */}
        {hasUserData && (
          <CareerSnapshot
            currentUser={currentUser}
            resolvedIds={resolvedIds}
            resolvedSkills={resolvedSkills}
            userSalary={userSalary}
            userPercentile={userPercentile}
            currentEmployerHealth={currentEmployerHealth}
            totalRoleSkills={roleSkills.length}
            isLoading={isResolving}
          />
        )}

        {/* Track Toggle */}
        <TrackToggle activeTrack={activeTrack} onTrackChange={setActiveTrack} />

        {/* ═══ TRACK A: Optimize Current Path ═══ */}
        {activeTrack === 'optimize' && (
          <VStack spacing={6} align="stretch">
            {/* A1: Your Pay Context */}
            {hasUserData && (
              <SalarySection
                profileData={currentProfile}
                resolvedIds={resolvedIds}
                rics={rics}
                isResolving={isResolving}
                salaryToggles={salaryToggles}
                onToggleChange={handleToggleChange}
                userSalary={userSalary}
                salaryTypeLabel={salaryTypeLabel}
                isSimulatedSalary={isSimulatedSalary}
                selectedUser={currentUser}
              />
            )}

            {/* A2: Skills That Pay */}
            {hasUserData && (
              <SkillPayImpactSection
                profileData={currentProfile}
                selectedUser={currentUser}
                resolvedIds={resolvedIds}
                resolvedSkills={resolvedSkills}
                unresolvedSkills={unresolvedSkills}
                isSkillsLoading={isSkillsLoading}
                isResolving={isResolving}
                userSalary={userSalary}
                upliftMode={upliftMode}
                userPercentile={userPercentile}
              />
            )}

            {/* A3: Employer Outlook */}
            {hasUserData && hasCompany && (
              <EmployerOutlookSection
                currentEmployerHealth={currentEmployerHealth}
                companyName={resolvedIds.companyName}
                sentimentEffectsData={sentimentEffectsData}
                sentimentEffectsLoading={sentimentEffectsLoading}
                currentCompanyMetrics={currentCompanyMetrics}
                competitorMetrics={metrics}
                sentimentOverall={
                  sentiments.find(
                    (s) => s.companyId === resolvedIds.companyRcid
                  )?.scores.overall ?? null
                }
                careerMoveSteps={careerMoveSteps}
                userSalary={userSalary}
                userRoleLevel={userRoleLevel}
                isDataLoading={isDataLoading}
                isResolving={isResolving}
                metrics={metrics}
              />
            )}
          </VStack>
        )}

        {/* ═══ TRACK B: Explore New Directions ═══ */}
        {activeTrack === 'explore' && (
          <VStack spacing={6} align="stretch">
            {/* B1: Find a New Company */}
            {hasUserData && hasCompany && (
              <BetterEmployersSection
                metrics={metrics}
                sentiments={sentiments}
                healthData={healthData}
                competitors={competitors}
                currentCompanyId={resolvedIds.companyRcid}
                hiddenGems={hiddenGems}
                leverageSignals={leverageSignals}
                leverageRoleName={leverageRoleName}
                signalsLoading={signalsLoading}
                signalsError={signalsError}
                workHistoryCompanies={workHistoryCompanies}
                isLoading={isTableLoading}
                isResolving={isResolving}
                userSalary={userSalary}
                userRoleLevel={userRoleLevel}
                isIndustryFallback={isIndustryFallback}
                selectedCompanyRcid={competitorSourceOverride}
                onCompanySelect={handleCompetitorSourceChange}
              />
            )}

            {/* B2: Where Could You Go? */}
            {hasUserData && hasRole && (
              <CareerTrajectorySection
                profileData={currentProfile}
                resolvedIds={resolvedIds}
                resolvedSkills={resolvedSkills}
                isResolving={isResolving}
                isSkillsLoading={isSkillsLoading}
                userSalary={userSalary}
                upliftMode={upliftMode}
                userPercentile={userPercentile}
              />
            )}

            {/* B2: Geographic Frontier */}
            {hasUserData && hasRole && (
              <GeographicMobilitySection
                resolvedIds={resolvedIds}
                rics={rics}
                isResolving={isResolving}
                userSalary={userSalary}
                upliftMode={upliftMode}
                userPercentile={userPercentile}
                userRoleLevel={userRoleLevel}
              />
            )}
          </VStack>
        )}
      </VStack>

      <CompareSelectionTray />
      <CompanyCompareDrawer
        userSalary={userSalary}
        userRoleLevel={userRoleLevel}
        userGeoId={userPrimaryGeo?.id ?? null}
        userGeoType={userPrimaryGeo?.type ?? null}
        userPercentile={userPercentile}
      />
    </>
  );
};
